import React from "react";
import { useFormat } from "hooks/useFormat";
import { Box, Stack, useTheme } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Typography from "components/basic/typography";

import { MessageAttachment, MessageView } from "../../../../types";

interface MessageBubbleProps {
  message: MessageView;
  isMine: boolean;
}

const isImage = (name: string) => /\.(png|jpe?g|gif|webp|svg)$/i.test(name);

const AttachmentChip: React.FC<{
  attachment: MessageAttachment;
  isMine: boolean;
}> = ({ attachment, isMine }) => {
  const theme = useTheme();

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "none", color: "inherit" }}
    >
      {isImage(attachment.name) ? (
        <img
          src={attachment.url}
          alt={attachment.name}
          style={{
            maxWidth: 200,
            maxHeight: 200,
            borderRadius: theme.shape.borderRadius,
            display: "block",
          }}
        />
      ) : (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            backgroundColor: isMine
              ? "rgba(255, 255, 255, 0.2)"
              : theme.palette.action.hover,
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <InsertDriveFileIcon sx={{ fontSize: 16 }} />
          <Typography
            variant="body2"
            style={{ wordBreak: "break-all", textDecoration: "underline" }}
          >
            {attachment.name}
          </Typography>
        </Stack>
      )}
    </a>
  );
};

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isMine }) => {
  const theme = useTheme();
  const format = useFormat();

  return (
    <Box
      display="flex"
      justifyContent={isMine ? "flex-end" : "flex-start"}
      px={2}
      py={0.5}
    >
      <Box maxWidth="70%">
        {!isMine && (
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary, marginLeft: 8 }}
          >
            {message.senderName}
          </Typography>
        )}
        <Box
          sx={{
            backgroundColor: isMine
              ? theme.palette.primary.main
              : theme.palette.action.hover,
            color: isMine
              ? theme.palette.primary.contrastText
              : theme.palette.text.primary,
            borderRadius: 2,
            px: 1.5,
            py: 1,
          }}
        >
          {message.body && (
            <Typography
              variant="body1"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {message.body}
            </Typography>
          )}
          {message.attachments.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: message.body ? 1 : 0 }}>
              {message.attachments.map((attachment, idx) => (
                <AttachmentChip
                  key={idx}
                  attachment={attachment}
                  isMine={isMine}
                />
              ))}
            </Stack>
          )}
        </Box>
        <Typography
          variant="caption"
          style={{
            color: theme.palette.text.secondary,
            display: "block",
            textAlign: isMine ? "right" : "left",
            margin: "2px 8px 0",
          }}
        >
          {format.timestamp(message.createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};

export default MessageBubble;
